import { useState, useEffect, useCallback } from 'react';
import { useApp, useAppState } from '../../../context/AppContext';
import CampaingsAPI from '../../../api/campaing';
import { useErrorHandler } from '../../../shared/hooks/useErrorHandler';
import NavigationService from '../../../utils/history';
import { IconButton } from '../../../insfrastructure/ui/button';

/**
 * Hook personalizado para manejar campañas
 * Reemplaza CampaingController
 * 
 * @returns {Object} Objeto con datos y funciones para manejar campañas
 */
export const useCampaings = () => {
    const { dispatch, notify } = useApp();
    const campaings = useAppState('campaings');
    const vcampaing = useAppState('vcampaing');
    const handleError = useErrorHandler();
    
    const [data, setData] = useState(campaings || []);
    const [view, setView] = useState(vcampaing || null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentId, setCurrentId] = useState(null);
    const [removeModal, setRemoveModal] = useState(false);
    const [current, setCurrent] = useState(false);

    // Columnas para la tabla
    const columns = [
        { key: 'title', label: 'Nombre', style: { width: "100%" } },
        { key: 'segment', label: 'Programa', style: { width: "100%" } },
        { key: 'actions', label: 'Acciones' }
    ];

    // Custom elements para la tabla
    const customElements = {
        actions: (data) => (
            <div style={{ width: 120 }}>
                <IconButton onClick={() => goView(data._id)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M15.58 12C15.58 13.98 13.98 15.58 12 15.58C10.02 15.58 8.42004 13.98 8.42004 12C8.42004 10.02 10.02 8.42004 12 8.42004C13.98 8.42004 15.58 10.02 15.58 12Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M12 20.27C15.53 20.27 18.82 18.19 21.11 14.59C22.01 13.18 22.01 10.81 21.11 9.39997C18.82 5.79997 15.53 3.71997 12 3.71997C8.46997 3.71997 5.17997 5.79997 2.88997 9.39997C1.98997 10.81 1.98997 13.18 2.88997 14.59C5.17997 18.19 8.46997 20.27 12 20.27Z" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
                <IconButton onClick={() => setCurrent(data)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M11 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22H15C20 22 22 20 22 15V13" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M16.04 3.02001L8.16 10.9C7.86 11.2 7.56 11.79 7.5 12.22L7.07 15.23C6.91 16.32 7.68 17.08 8.77 16.93L11.78 16.5C12.2 16.44 12.79 16.14 13.1 15.84L20.98 7.96001C22.34 6.60001 22.98 5.02001 20.98 3.02001C18.98 1.02001 17.4 1.66001 16.04 3.02001Z" stroke="#292D32" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M14.91 4.1499C15.58 6.5399 17.45 8.4099 19.85 9.0899" stroke="#292D32" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
                <IconButton onClick={() => openRemove(data._id)}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M21 5.97998C17.67 5.64998 14.32 5.47998 10.98 5.47998C9 5.47998 7.02 5.57998 5.04 5.77998L3 5.97998" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M8.5 4.97L8.72 3.66C8.88 2.71 9 2 10.69 2H13.31C15 2 15.13 2.75 15.28 3.67L15.5 4.97" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M18.85 9.14001L18.2 19.21C18.09 20.78 18 22 15.21 22H8.79002C6.00002 22 5.91002 20.78 5.80002 19.21L5.15002 9.14001" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M10.33 16.5H13.66" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                        <path d="M9.5 12.5H14.5" stroke="#292D32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </IconButton>
            </div>
        ),
    };

    // Sincronizar con estado global
    useEffect(() => {
        if (campaings) setData(campaings);
    }, [campaings]);

    useEffect(() => {
        if (vcampaing) setView(vcampaing);
    }, [vcampaing]);

    /**
     * Convertir objeto a FormData
     */
    const objectToFormData = useCallback((data) => {
        const fd = new FormData();
        Object.keys(data).forEach((key) => {
            const value = data[key];
            if (value) {
                if (Array.isArray(value)) {
                    value.forEach((v) => fd.append(`${key}[]`, v));
                } else if (value instanceof File || value instanceof Blob) {
                    fd.append(key, value);
                } else if (value !== undefined && value !== null) {
                    fd.append(key, value);
                }
            }
        });
        return fd;
    }, []);

    /**
     * Obtener todas las campañas
     */
    const getAll = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await CampaingsAPI.getCampaings();
            const campaingsList = Array.isArray(res) ? res : [];
            
            dispatch({ type: 'SET_CAMPAINGS', payload: campaingsList });
            setData(campaingsList);
            return campaingsList;
        } catch (error) {
            handleError(error, 'Error al obtener las campañas');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [dispatch, handleError]);

    /**
     * Obtener campaña por ID
     */
    const getById = useCallback(async (id) => {
        if (!id) return null;

        setIsLoading(true);
        try {
            // Intentar obtener del estado local primero (optimización)
            const currentCampaings = campaings || [];
            const foundCampaing = currentCampaings.find(e => e._id === id);
            
            if (foundCampaing) {
                dispatch({ type: 'SET_VCAMPAING', payload: foundCampaing });
                setView(foundCampaing);
                setCurrentId(id);
            }

            // Siempre obtener del servidor para asegurar datos actualizados
            const res = await CampaingsAPI.getCampaingById(id);
            const campaingData = res.data || res;
            
            dispatch({ type: 'SET_VCAMPAING', payload: campaingData });
            setView(campaingData);
            setCurrentId(id);
            return campaingData;
        } catch (error) {
            handleError(error, 'Error al obtener la campaña');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [campaings, dispatch, handleError]);

    /**
     * Crear nueva campaña
     */
    const create = useCallback(async (data) => {
        setIsLoading(true);
        try {
            const formData = objectToFormData(data);
            const res = await CampaingsAPI.createCampaing(formData);
            await getAll(); // Refrescar lista
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Campaña creada correctamente" 
            });
            return res;
        } catch (error) {
            handleError(error, 'Error al crear la campaña');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [getAll, notify, handleError, objectToFormData]);

    /**
     * Actualizar campaña
     */
    const update = useCallback(async (id, data) => {
        setIsLoading(true);
        try {
            const formData = objectToFormData(data);
            const res = await CampaingsAPI.updateCampaing(id, formData);
            await getAll(); // Refrescar lista
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Campaña actualizada correctamente" 
            });
            return res;
        } catch (error) {
            handleError(error, 'Error al actualizar la campaña');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [getAll, notify, handleError, objectToFormData]);

    /**
     * Eliminar campaña
     */
    const remove = useCallback(async () => {
        if (!currentId) {
            notify({ 
                title: "Error", 
                type: "error", 
                text: "No se ha seleccionado una campaña para eliminar" 
            });
            return;
        }

        setIsLoading(true);
        try {
            const res = await CampaingsAPI.deleteCampaingById(currentId);
            await getAll(); // Refrescar lista
            setRemoveModal(false);
            setCurrentId(null);
            notify({ 
                title: "Éxito!", 
                type: "success", 
                text: "Campaña eliminada correctamente" 
            });
            return res;
        } catch (error) {
            handleError(error, 'Error al eliminar la campaña');
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [currentId, getAll, notify, handleError]);

    /**
     * Crear o actualizar campaña (wrapper para mantener compatibilidad)
     */
    const createOrUpdate = useCallback(async (body) => {
        const { isEditing, ...data } = body;
        if (isEditing && data.id) {
            await update(data.id, data);
        } else {
            await create(data);
        }
    }, [create, update]);

    /**
     * Navegar a vista de campaña
     */
    const goView = useCallback((id) => {
        setCurrentId(id);
        NavigationService.navigateTo(`/cpt/campaings/${id}`);
    }, []);

    /**
     * Navegar a crear nueva campaña
     */
    const newItem = useCallback(() => {
        NavigationService.navigateTo("/cpt/campaing/create");
    }, []);

    /**
     * Navegar atrás
     */
    const goBack = useCallback(() => {
        NavigationService.navigateBack();
    }, []);

    /**
     * Abrir modal de eliminación
     */
    const openRemove = useCallback((id) => {
        setRemoveModal(true);
        setCurrentId(id);
    }, []);

    return {
        // Datos
        data,
        campaings,
        view,
        vcampaing,
        columns,
        customElements,
        
        // Estado
        isLoading,
        currentId,
        removeModal,
        setRemoveModal,
        current,
        setCurrent,
        
        // Funciones CRUD
        getAll,
        getById,
        create,
        update,
        remove,
        createOrUpdate,
        
        // Navegación
        goView,
        newItem,
        goBack,
        openRemove,
        
        // Helpers
        refresh: getAll,
    };
};

export default useCampaings;

